import { CardGridSkeleton } from "../skeletons";
/**
 * Spotlight's catalogue pages (Courses, Community): an open page — title,
 * subtitle and search, then an "Explore" section holding the grid — beside the
 * membership rail (community card, My Points, levels). No card wrapped around
 * the list; the banner above already frames the page.
 *
 * Presentational only. The views own the data and routing and hand in the
 * pieces: the grid itself (the same cards every template uses), the search, and
 * the rail.
 */

/** Page title, one line of context, and the page's search/filter row. */
export function SpotlightIntro({ title, subtitle, search }) {
  return (
    <header className="mb-8">
      <h2 className="text-[26px] font-bold tracking-tight text-gray-900 sm:text-[28px]">{title}</h2>
      {subtitle ? <p className="mt-1 max-w-2xl text-[14px] text-gray-500">{subtitle}</p> : null}
      {search ? <div className="mt-5 max-w-xl">{search}</div> : null}
    </header>
  );
}

/** The section heading ("Explore" by default) and its count line, over whatever follows. */
export function SpotlightExplore({ title = "Explore", caption, children }) {
  return (
    <section aria-label={title}>
      <div className="mb-4">
        <h3 className="text-[18px] font-semibold tracking-tight text-gray-900">{title}</h3>
        {caption ? <p className="mt-0.5 text-[13.5px] text-gray-500">{caption}</p> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Content left, membership rail right — from 1440px up (see spotlight.css);
 * below that the rail stacks under the content so the cards keep their width.
 */
export function SpotlightRailLayout({ rail, children }) {
  return (
    <div className="tpl-rail-grid">
      <div className="order-1 min-w-0">{children}</div>
      {rail ? (
        <aside className="order-2 space-y-4 scrollbar-hide min-[1440px]:sticky min-[1440px]:top-20 min-[1440px]:max-h-[calc(100vh-6rem)] min-[1440px]:self-start min-[1440px]:overflow-y-auto min-[1440px]:pr-1">
          {rail}
        </aside>
      ) : null}
    </div>
  );
}

/** Grid columns for a catalogue beside the rail — cards never drop below ~340px. */
export const SPOTLIGHT_GRID = "grid grid-cols-1 gap-5 sm:grid-cols-2 min-[1760px]:grid-cols-3";

/**
 * Placeholder grid shown while the catalogue loads.
 *
 * `count` should be the page size, not a token number. The skeleton's job is to
 * reserve the space the real cards are about to take, and two cards under a
 * full-width "Explore" heading read as "this membership has two courses" — then
 * the page jumps when six arrive.
 *
 * The card itself now lives in src/skeletons.jsx, which the other six templates
 * share: this shape was the one that worked, so it became the shape everywhere
 * rather than a seventh variation of it.
 */
export function SpotlightGridSkeleton({ count = 6 }) {
  return <CardGridSkeleton count={count} grid={SPOTLIGHT_GRID} />;
}

export default function SpotlightCourses({ courses, loading, total, communityName, search, topSlot, empty, pagination, grid, rail, skeletonCount = 6 }) {
  const count = total ?? courses?.length ?? 0;

  return (
    <SpotlightRailLayout rail={rail}>
      <SpotlightIntro
        title="Courses"
        subtitle={`Every course in ${communityName || "this membership"} — pick up where you left off, or start something new.`}
        search={search}
      />
      {topSlot}
      <SpotlightExplore caption={loading ? "Loading the catalogue…" : `${count} ${count === 1 ? "course" : "courses"}, all in one place.`}>
        {loading ? (
          <SpotlightGridSkeleton count={skeletonCount} />
        ) : !courses || courses.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-surface">{empty}</div>
        ) : (
          grid
        )}
        {pagination}
      </SpotlightExplore>
    </SpotlightRailLayout>
  );
}
